import {
  Alert,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@commenda/kakashi';
import { WorkflowStepDescription } from '../../../components';
import { TaskStatus } from '@prisma/client';
import { useMemo, useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { JsonSchemaForm } from '../../../json-schema-form/JsonSchemaForm';
import { TCommonTaskProp } from '../../types';
import { updateTaskStatus } from '../../utils';

type OutputSchemaModalProps = {
  isOpen: boolean;
  onClose: () => void;
  schema: Record<string, any>;
  title: string;
  description?: string;
  task: TCommonTaskProp['task'];
  context: TCommonTaskProp['context'];
};

export const OutputSchemaModal = ({
  isOpen,
  onClose,
  schema,
  title,
  description,
  task,
  context,
}: OutputSchemaModalProps) => {
  const { setNotification } = useNotification();
  const [formData, setFormData] = useState<Record<string, unknown>>();

  const defaultData = useMemo(() => {
    if (context.workflowExecution?.runtime?.stepExecutions) {
      const stepExecution = Object.values(
        context.workflowExecution.runtime.stepExecutions
      ).find(
        (execution) =>
          execution.stepDefinitionId === task.workflowDefinitionStepId
      );
      return stepExecution?.outputData;
    }

    return undefined;
  }, [context.workflowExecution, task.workflowDefinitionStepId]);

  // This is demo code. It will be removed later.
  const effectiveData = formData ?? defaultData;
  const showTM01Alert =
    'alreadyFiledWithTheCompanyHouse' in (schema.properties ?? {}) &&
    effectiveData?.alreadyFiledWithTheCompanyHouse === false;
  const showUBOAlert =
    'removeThePersonAsUBO' in (schema.properties ?? {}) &&
    effectiveData?.removeThePersonAsUBO === true;

  const onSubmit = async (data: Record<string, any>) => {
    const result = await updateTaskStatus({
      task,
      mutate: context.mutate,
      status: TaskStatus.COMPLETED,
      metadata: {
        outputData: data,
      },
    });

    if (!result) {
      setNotification({
        title: 'Error',
        message: 'Failed to complete task',
        valid: false,
        show: true,
      });
      return;
    }

    setNotification({
      title: 'Success',
      message: 'Task completed successfully',
      valid: true,
      show: true,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader className='flex flex-col items-start gap-1'>
          <DialogTitle>{title}</DialogTitle>
          <WorkflowStepDescription description={description} />
        </DialogHeader>
        <div className="p-3xl flex flex-col gap-3xl">
          {showTM01Alert && (
            <Alert
              variant="info"
              description="If you select “No”, we will file a TM01 form with Companies House to remove the director on your behalf."
            />
          )}
          {showUBOAlert && (
            <Alert
              variant="info"
              description="If you select “Yes”, we will file a UBO form to remove the director as a beneficial owner on your behalf."
            />
          )}

          <JsonSchemaForm
            schema={schema}
            onSubmit={onSubmit}
            defaultData={defaultData}
            onChange={setFormData}
            onCancel={onClose}
            readonly={task.status === TaskStatus.COMPLETED}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
